import { useScreenSize } from 'common/hooks/useScreenSize'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  TouchEvent,
  MouseEvent,
  CSSProperties,
} from 'react'

interface UseSliderProps {
  gap?: number
  slidesCount?: number
}

export const useSlider = ({ slidesCount = 4, gap = 10 }: UseSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [startX, setStartX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [translateX, setTranslateX] = useState(0)
  const [isDisabled, setIsDisabled] = useState(true)
  const sliderRef = useRef<HTMLDivElement>(null)
  const screenSize = useScreenSize()
  const getStyles = (): CSSProperties => ({
    transform: `translateX(calc(-${currentIndex * 100}% + ${translateX}% - ${currentIndex * gap}%))`,
    transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
  })
  const [sliderStyles, setSliderStyles] = useState<CSSProperties>(getStyles)

  const goToNext = useCallback(() => {
    if (isDisabled) return
    setCurrentIndex(prev => (prev + 1) % slidesCount)
    setTranslateX(0)
  }, [slidesCount, isDisabled])

  const goToPrev = useCallback(() => {
    if (isDisabled) return
    setCurrentIndex(prev => (prev - 1 + slidesCount) % slidesCount)
    setTranslateX(0)
  }, [slidesCount, isDisabled])

  const handleTouchStart = (e: TouchEvent) => {
    if (isDisabled || isDragging) return
    setIsDragging(true)
    setStartX(e.touches[0].clientX)
  }

  const handleMouseDown = (e: MouseEvent) => {
    if (isDisabled || isDragging) return
    setIsDragging(true)
    setStartX(e.clientX)
    e.preventDefault()
  }

  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging || !sliderRef.current) return
      const deltaX = clientX - startX
      const sliderWidth = sliderRef.current.offsetWidth
      const sensitivity = 1.5
      setTranslateX((deltaX / sliderWidth) * 100 * sensitivity)
    },
    [isDragging, startX],
  )

  const handleTouchMove = (e: TouchEvent) => {
    if (isDisabled) return
    handleMove(e.touches[0].clientX)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || isDisabled) return
    handleMove(e.clientX)
  }

  const handleEnd = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = 5
    if (Math.abs(translateX) > threshold) {
      if (translateX > 0) {
        goToPrev()
      } else {
        goToNext()
      }
    } else {
      setTranslateX(0)
    }
  }, [isDragging, translateX, goToNext, goToPrev])

  useEffect(() => {
    const isMobile = screenSize.width <= 800
    setIsDisabled(!isMobile)
    if (!isMobile && currentIndex !== 0) {
      setCurrentIndex(0)
    }
  }, [screenSize.width, currentIndex])

  useEffect(() => {
    setSliderStyles(getStyles)
  }, [isDragging, translateX, currentIndex])

  return {
    goToNext,
    goToPrev,
    sliderRef,
    currentIndex,
    handleTouchStart,
    handleTouchMove,
    handleMouseMove,
    handleMouseDown,
    handleEnd,
    handleMove,
    isDragging,
    translateX,
    sliderStyles,
  }
}
